/**
 * RWBR — the shared vocabulary every rule module speaks.
 *
 * Two things live here and nothing else:
 *
 *   1. THE EVENT ENVELOPE. Every rule resolver returns an object that is, by
 *      itself, a complete ledger entry: what rule fired, which page of the
 *      rulebook says so, which dice produced it, every modifier that was
 *      applied and why, who moved and from where to where, and a sentence a
 *      human can read in the broadcast booth. A result that cannot be replayed
 *      from its own fields is a bug.
 *
 *   2. FIELD GEOMETRY HELPERS. Read-only views over the running order. Rule
 *      modules never mutate a `RaceState` or a `Car` — they build a working
 *      copy of the running order, move cards around inside it, and return the
 *      difference. `types.ts` owns what a group IS; this owns how you look
 *      things up in one.
 *
 * FILLING A GAP (rulebook p.vi) IS NOT DONE HERE. When a card leaves a LEADER,
 * TOP or MIDDLE slot, the vacancy is filled at the END of the turn — under
 * yellow by promoting the front of the next group, under green by rolling the
 * red die against each candidate's gas pedal. That is a turn-level procedure
 * with its own dice, so these modules report the vacancy and let the caller
 * run it. Anything else would invent movement the rulebook does not sanction.
 */

import type { Car, Group, RaceState } from './types.ts';
import { groupOf } from './types.ts';
import type { ChipHold } from './chips.ts';
import { forfeitDelta, reserveAfterMove, scopeChips } from './chips.ts';

// ---------------------------------------------------------------------------
// The event envelope
// ---------------------------------------------------------------------------

/**
 * ONE ENVELOPE, NOT TWO.
 *
 * The challenge/duel/feud family and the repair/trouble/problem/rare family
 * were written independently and grew near-identical ledger types. What follows
 * is the SUPERSET of the two, and it is the only one. Where the two disagreed,
 * the richer option won:
 *
 *   • `dice` is an ARRAY of tagged rolls, not a bag of named faces. A turn can
 *     roll the same notation several times (six ESCAPE checks, a chain of
 *     11-66 reads) and the ledger has to keep them in order, each labelled with
 *     the notation it was READ in — 2d6 SUM and the 11-66 two-digit code are
 *     different readings of the same two dice (dice.ts) and conflating them has
 *     already caused one bug in this project's source material.
 *   • `Modifier.value` accepts a string, so "repair table: problem" and
 *     "bonus brake symbols: +2" can live in the same list.
 *   • `PositionDelta` carries a `placement`, because an incident module knows a
 *     car is going to "the END of the BOTTOM group" without knowing what number
 *     that is until the rest of the turn has been applied.
 *
 * `LedgerEvent` is kept as an alias of `RuleEvent` — one shape, two names, so
 * neither family's call sites had to be rewritten to say something new.
 */

/**
 * One named, valued adjustment. Every number a rule adds to or subtracts from
 * a printed rating appears here — including the negative entry recorded when
 * the five-brake-symbol ceiling (p.vii) claws a bonus back.
 */
export interface Modifier {
  /** Stable kebab-case identifier. Tests assert on these. */
  name: string;
  /** Signed contribution, or the named value that was in force. */
  value: number | string;
  /** Why it applied, in plain language. */
  note?: string;
  /** Same job as `note`; both spellings are carried so neither family lies. */
  detail?: string;
}

/**
 * Where a card ends up when the rule that moved it cannot know the number.
 * `end-of-bottom` is the rulebook's own phrase (p.iv, p.v, p.xii).
 */
export type Placement =
  | 'hold'
  | 'out-of-race'
  | 'end-of-bottom'
  | 'front-of-bottom'
  | 'lead'
  | 'top-group'
  | 'middle-group'
  /**
   * The end of the laps-down sub-group — the very back of the field, off the
   * lead lap. Distinct from `end-of-bottom` because once the LDSG exists those
   * are two different places, and every rule that says one means only one.
   */
  | 'laps-down'
  /**
   * The last position ON the lead lap: directly above the LDSG boundary. Where
   * the Lucky Dog, a won laps-down challenge and Appendix B2's "returns as the
   * last driver on the lead lap" all put a car.
   */
  | 'last-of-lead-lap';

/**
 * A card that moved.
 *
 * `placement` is authoritative when present — it is how an incident module says
 * "the END of the BOTTOM group" without counting cars. When it is absent, `to`
 * is authoritative: the rule computed the slot itself (a challenge swap, a feud
 * demotion), and `to: null` means out of the race.
 */
export interface PositionDelta {
  driverId: string;
  /** 1-based position before the rule fired. */
  from: number;
  /** 1-based position after, or null if the car is out or the slot is not knowable here. */
  to: number | null;
  placement?: Placement;
  note?: string;
}

/**
 * The notation a roll was READ in. These do not mix — see dice.ts.
 *
 * `d10` arrives with the EZRA injury tables, the only sheet in any of this
 * material that asks for a ten-sided die ("ROLL 1D10" — how many races he is
 * out for).
 */
export type DiceNotation = '2d6-sum' | '11-66' | 'd6' | 'd6-set' | 'd10';

/** One roll, exactly as it came up, in the notation it was read in. */
export interface DiceRecord {
  kind: DiceNotation;
  /** The faces, in the order they were rolled. */
  dice: number[];
  /** The 2d6 sum, for kind '2d6-sum'. */
  total?: number;
  /** The two-digit code, for kind '11-66'. */
  code?: string;
  /** What this roll was for: 'ESCAPE check', 'challenge red-white-blue', … */
  label?: string;
  /** Which die played which role: `{ red: 4, white: 2, blue: 3 }`. */
  faces?: Record<string, number>;
  /** Reads taken off the faces: `{ triple: false, doubles: true }`. */
  flags?: Record<string, boolean>;
  /**
   * D-018 — WHO PRODUCED THESE FACES.
   *
   *   absent  — the seeded engine rolled them. The overwhelmingly common case,
   *             and deliberately the ABSENT one: a seeded race's ledger is then
   *             byte-for-byte what it was before manual entry existed, which is
   *             what the determinism guarantee is made of.
   *   'hand'  — a human rolled real dice at a real table and typed them in.
   *   'mixed' — this turn straddled the crossover from hand-fed faces to the
   *             PRNG (`ExhaustionPolicy: 'prng'`). Reported rather than rounded
   *             to one or the other, because it is a THIRD fact and picking a
   *             side would be inventing one.
   *
   * STAMPED PER TURN, NOT PER ROLL — and that is exact rather than approximate;
   * see the note in `runTurn`. The face-by-face record is `Dice.stream`, carried
   * on `RaceResult.diceStream`, and the header comment in `dice.ts` sets out,
   * with the measurements, why the per-face mark cannot live on this type.
   */
  entry?: 'hand' | 'mixed';
}

/** A chip earned, spent or forfeited. */
export interface ChipDelta {
  driverId: string;
  /** +1 earned, -1 spent or forfeited. */
  delta: number;
  reason: string;
}

/**
 * A BGC performance chip destroyed by its owner leaving the BOTTOM group
 * (EZRA-S2 §3.12, D-EZ-39).
 *
 * NOT a `ChipDelta`. A BG chip is *"a token of a different color from that used
 * for PC's in the Middle and Top Groups"*, and filing its loss as a performance
 * chip delta would make the ledger claim a driver lost something he never held —
 * the same distinction that keeps a trait-chip suspension out of the deltas.
 *
 * The positions are carried because they are the whole justification for the
 * loss: `from` is inside the BOTTOM group and `to` is not.
 */
export interface BgChipLoss {
  driverId: string;
  chips: number;
  from: number;
  to: number;
}

/**
 * A PROBLEM this rule triggered but did not resolve. PROBLEM FINDER, the
 * repair roll and the yellow flag that may follow are the problem module's
 * job; the challenge only says who and why.
 */
export interface PendingProblem {
  driverId: string;
  /** `triples` = red/white/blue all equal (p.vi). `triangle` = red 6 on a triangle car (p.xvi). */
  source: 'triples' | 'triangle';
  /**
   * True when the PROBLEM FINDER chart must be rolled for a specific problem;
   * false for the triangle symbol, which is an *unspecified* problem that goes
   * straight to a repair roll (p.xvi).
   */
  specified: boolean;
  ruleCitation: string;
  description: string;
}

/**
 * A TROUBLE result this rule triggered but did not resolve. Escape rolls and
 * repair rolls belong to the trouble module.
 *
 * A duel puts exactly one named driver in trouble (p.ix). A feud/fume result
 * puts a whole group in trouble (p.x), which is why the scope varies.
 */
export type PendingTrouble =
  | { scope: 'driver'; driverId: string; source: string; ruleCitation: string }
  | { scope: 'group'; group: Group; source: string; ruleCitation: string };

/** The ledger row. Every resolver's result extends this. */
export interface RuleEvent {
  /** Coarse kind, for filtering: `quality-challenge`, `duel`, `feud-fume`, … */
  eventType: string;
  /** Fine-grained story beat: `challenge.pass`, `duel.win`, `feud.wreck`, … */
  momentTag: string;
  /** Rulebook page in `RB/<roman>` form, e.g. `RB/vii`. Board charts add `+ Board A`. */
  ruleCitation: string;
  /** Everyone the moment is about, instigator/challenger first. */
  actorIds: string[];
  /** Only the cards THIS rule moved. See the note about FILLING A GAP above. */
  positionDeltas: PositionDelta[];
  /** Every modifier applied, named and valued. */
  modifiers: Modifier[];
  /** Every roll this rule made, in the order it made them. */
  dice: DiceRecord[];
  /** One or two sentences, readable aloud. */
  description: string;
}

/** The same envelope under the incident modules' name for it. */
export type LedgerEvent = RuleEvent;

// ---------------------------------------------------------------------------
// Reading the ledger back
// ---------------------------------------------------------------------------

/** A modifier's numeric contribution. A named (string) value contributes nothing. */
export function modifierValue(m: Modifier): number {
  return typeof m.value === 'number' ? m.value : 0;
}

/** The first roll carrying `label`, or undefined. */
export function diceByLabel(rolls: readonly DiceRecord[], label: string): DiceRecord | undefined {
  return rolls.find((r) => r.label === label);
}

/** The face a named die showed, searched across every roll in the event. */
export function faceOf(rolls: readonly DiceRecord[], name: string): number | undefined {
  for (const roll of rolls) {
    const v = roll.faces?.[name];
    if (v !== undefined) return v;
  }
  return undefined;
}

/** Every face in the event, flattened — for "how many dice did this cost?" checks. */
export function allFaces(rolls: readonly DiceRecord[]): number[] {
  return rolls.flatMap((r) => r.dice);
}

/**
 * D-018 — mark a batch of ledger rows with who produced their faces.
 *
 * Called once per turn by `runTurn` with the verdict `Dice.entryOver` returned
 * for that turn's whole window of the stream. `null` means "seeded race,
 * nothing to say" and is a no-op, which is what keeps a seeded ledger
 * byte-identical to the one this project has been producing all along.
 *
 * MUTATES IN PLACE, deliberately. These records were built moments ago by the
 * rule modules of the turn that is still being assembled, and nothing outside
 * has seen them yet; rebuilding every event to add one field would churn object
 * identity that the turn's own bookkeeping (`absorb` pushes the SAME object into
 * `events` that it reads `positionDeltas` off) relies on.
 */
export function stampDiceEntry(
  events: readonly RuleEvent[],
  entry: 'engine' | 'hand' | 'mixed' | null,
): number {
  if (entry === null || entry === 'engine') return 0;
  let stamped = 0;
  for (const event of events) {
    for (const roll of event.dice ?? []) {
      roll.entry = entry;
      stamped++;
    }
  }
  return stamped;
}

// ---------------------------------------------------------------------------
// Field geometry
// ---------------------------------------------------------------------------

/**
 * A working copy of the running order, index 0 = leader.
 *
 * The `Car` objects inside are the caller's own — never write to them. Move
 * cards by rearranging the array, exactly as you would rearrange cards on the
 * board, then diff it.
 */
export function fieldFromState(state: RaceState): Car[] {
  const order = [...state.grid].sort((a, b) => a.position - b.position);
  order.forEach((car, i) => {
    if (car.position !== i + 1) {
      throw new Error(
        `RaceState.grid is not a contiguous running order: ${car.card.name} is at ` +
          `position ${car.position} but is the ${i + 1}${i === 0 ? 'st' : 'th'} car. ` +
          `Positions must be 1..n with no gaps or ties before any rule is resolved.`,
      );
    }
  });
  return order;
}

/** 1-based position. Throws if the driver is not in this field. */
export function positionOf(order: readonly Car[], driverId: string): number {
  const i = order.findIndex((c) => c.card.driverId === driverId);
  if (i === -1) throw new Error(`driver ${driverId} is not in the running order`);
  return i + 1;
}

/** The car at a 1-based position, or undefined if the field is shorter than that. */
export function carAt(order: readonly Car[], position: number): Car | undefined {
  return position >= 1 ? order[position - 1] : undefined;
}

/** Every car currently in a group, in running order. */
export function membersOf(order: readonly Car[], group: Group): Car[] {
  return order.filter((_, i) => groupOf(i + 1) === group);
}

/** The group a challenge from `group` is aimed at. The leader has nobody above him. */
export function groupAbove(group: Group): Group | null {
  if (group === 'leader') return null;
  if (group === 'top') return 'leader';
  if (group === 'middle') return 'top';
  return 'middle';
}

/** Swap two 1-based positions in a working order. Mutates the working array only. */
export function swapPositions(order: Car[], a: number, b: number): void {
  const i = a - 1;
  const j = b - 1;
  const tmp = order[i];
  order[i] = order[j];
  order[j] = tmp;
}

/**
 * Stamp a working order back into `Car.position`, 1..n, and settle the two
 * standing rules about chips at whatever position each card ended up in:
 *
 *   p.xv           — an ORDINARY chip cannot be carried in the BOTTOM group and
 *                    is forfeited on the way down.
 *   EZRA-S2 §4.0   — a chip awarded by a driver TRAIT is not lost, only parked,
 *                    and comes back when he reaches the MIDDLE or TOP group.
 *
 * Both live in `chips.ts`; this is just the place the field gets measured
 * against them. `holds` reports the parks and the hand-backs so the caller can
 * put them in the ledger — they are not chip DELTAS, because nothing was
 * created or destroyed, which is the entire point of the EZRA rule.
 *
 * Returns new `Car` objects for every card whose position or chip count moved;
 * untouched cards are passed through by reference, so `===` still means "did
 * not move".
 */
export function renumber(order: readonly Car[]): {
  order: Car[];
  forfeits: ChipDelta[];
  holds: ChipHold[];
  bgLosses: BgChipLoss[];
} {
  const forfeits: ChipDelta[] = [];
  const holds: ChipHold[] = [];
  const bgLosses: BgChipLoss[] = [];
  const out = order.map((car, i) => {
    const position = i + 1;
    // "If he loses track position for any reason, reserve pcs are lost."
    // (D-EZ-28.) Settled HERE because this is the one place a car's position
    // becomes a fact rather than a prediction — the same reason the BOTTOM-group
    // chip rules live here. `car.position` is where he was; `position` is where
    // the board just put him.
    const dropped = reserveAfterMove(car, car.position, position);
    const moved = scopeChips(dropped.car, position);
    const driverId = car.card.driverId;
    if (dropped.lost > 0) {
      forfeits.push({
        driverId,
        delta: -dropped.lost,
        reason: 'reserve performance chips are lost on losing track position (EZRA-S2)',
      });
    }
    if (moved.forfeited > 0) forfeits.push(forfeitDelta(driverId, moved.forfeited));
    if (moved.parked > 0) holds.push({ driverId, chips: moved.parked, restored: false });
    if (moved.restored > 0) holds.push({ driverId, chips: moved.restored, restored: true });
    // EZRA-S2 §3.12 — "Cars in BG with PCs that move to Middle Group during a
    // Regular Turn lose their BGC PC(s) when exiting the BG." Reported on its
    // OWN channel rather than folded into `forfeits`, because `forfeits` is a
    // list of `ChipDelta` denominated in performance chips and a BG chip is "a
    // token of a different color" — the same reason `holds` is not a delta.
    if (moved.bgLost > 0) bgLosses.push({ driverId, chips: moved.bgLost, from: car.position, to: position });
    if (car.position === position && moved.car === car && dropped.lost === 0) return car;
    return { ...moved.car, position };
  });
  return { order: out, forfeits, holds, bgLosses };
}

/** Position changes between two working orders. Cars absent from `after` are out. */
export function diffOrder(before: readonly Car[], after: readonly Car[]): PositionDelta[] {
  const deltas: PositionDelta[] = [];
  before.forEach((car, i) => {
    const id = car.card.driverId;
    const j = after.findIndex((c) => c.card.driverId === id);
    const to = j === -1 ? null : j + 1;
    if (to !== i + 1) deltas.push({ driverId: id, from: i + 1, to });
  });
  return deltas;
}

/** Display name for narration. */
export function nameOf(car: Car): string {
  return car.card.name;
}
